import { useEffect, useRef } from 'react';

/**
 * Keeps the screen awake while active (live scoring pad — sc-338). Uses the
 * standard Web Wake Lock API, supported in Capacitor's Chromium-based Android
 * WebView and modern mobile browsers; iOS WebKit support is newer and
 * inconsistent, so this silently no-ops where unsupported rather than adding
 * a native dependency for it.
 */
export function useWakeLock(enabled: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!enabled || !('wakeLock' in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      try {
        const sentinel = await (navigator as Navigator & { wakeLock: WakeLock }).wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
      } catch {
        // Denied (e.g. low battery) or unsupported in this context — scoring still works.
      }
    };

    void acquire();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) {
        void acquire();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void sentinelRef.current?.release();
      sentinelRef.current = null;
    };
  }, [enabled]);
}

// Minimal ambient types — lib.dom's Wake Lock types aren't universally present across TS lib targets.
interface WakeLockSentinel {
  release(): Promise<void>;
}
interface WakeLock {
  request(type: 'screen'): Promise<WakeLockSentinel>;
}
